import { DeviceLayout } from '@/hooks/useDeviceLayout';
import { BeatMappedChordProgression } from '@/javascript/chord-progression-identification';
import { TimelineSectionDropCompletedEvent, VisibleTimelineMeasuresChangedEvent } from '@/javascript/custom-events';
import MidiPlayer, { MidiPlayerTrack } from '@/javascript/midi-player';
import { Key, Section, Tempo } from '@/javascript/music-objects';
import { Project, ProjectTrackItem } from '@/javascript/project-objects';
import { Utils } from '@/javascript/utils';
import { Box, Button, Group, Text, Tooltip } from '@mantine/core';
import { IconArrowBackUp, IconArrowForwardUp, IconCopy, IconMarquee2, IconPlayerPlay, IconPlayerSkipBack, IconPlayerStop, IconScissors, IconTrash } from '@tabler/icons-react';
import React, { Component, MouseEventHandler } from 'react';
import { InstrumentName } from 'soundfont-player';
import ChordReductions from './ChordReductions';
import MeasureMarkers from './MeasureMarkers';
import TempoControl from './TempoControl';
import './Timeline.css';
import { Track } from './TimelineTrack';
import TimelineZoomBar from './TimelineZoomControl';

interface TimelineState {
	trackHeight: number;
	timelineHeight: number;
	measureWidth: number;
	isDragging: boolean;
	startX: number;
	startY: number;
	scrollLeft: number;
	scrollTop: number;
	selectedItemIds: string[];
	contextMenuVisible: boolean;
	contextMenuX: number;
	contextMenuY: number;
	contextMenuFadeOut: boolean;
	cursorMarkerMeasure: number;
	isPlaying: boolean;
	playingMeasureNumber: number;
	playbackLoading: boolean;
	chopMode: boolean;
	projectHistory: Project[];
	projectHistoryFuture: Project[];
	isMarqueeToggled: boolean;
	isMarqueeActive: boolean;
	marqueeStartX: number;
	marqueeStartY: number;
	marqueeEndX: number;
	marqueeEndY: number;
	isZooming: boolean;
	isControlHeld: boolean;
	isShiftHeld: boolean;
	notationView: boolean;
	chordProgressionsByMeasure: Map<number, { selection: BeatMappedChordProgression | undefined, options: BeatMappedChordProgression[] }>;
}

interface ScreenRect {
	left: number;
	top: number;
	width: number;
	height: number;
}

interface GhostItemLayout {
	sectionId: string;
	dragToken?: string;
	matchId: string;
	rect: ScreenRect;
}

interface TimelineProps {
	draggingSections: Section[] | null;
	setDraggingSections: (stems: Section[] | null) => void;
	setDragOverTimeline: (over: boolean) => void;
	updateProject: (project: Project, callback?: () => void, signal?: AbortSignal) => void;
	project: Project;
	viewOnly: boolean;
	beginnerTourActive: boolean;
	currentTourStep: number;
	moveToNextTourStep: () => void;
	moveToPrevTourStep: () => void;
	jumpToTourStep: (index: number) => void
	loadingInstrument: boolean;
	onGhostItemLayoutChange?: (layouts: GhostItemLayout[]) => void;
	deviceLayout: { deviceLayout: DeviceLayout; isPhoneOrSmaller: boolean; isTabletOrSmaller: boolean; }
}

class Timeline extends Component<TimelineProps, TimelineState> {
	public static trackLabelWidth: number = 100;

	private static projectHistoryDepth: number = 10; // for every action, we store up to this number of clones of the timeline tracks for undo functionality
	private static notationViewTrackHeight: number = 200;
	private static minTrackHeight: number = 50;
	private static measureMarkerHeight: number = 30;
	private static numberOfMeasures: number = 100;
	private static minMeasureWidth: number = 25; // Minimum pixel width for each measure
	private static maxMeasureWidth: number = 310; // Maximum pixel width for each measure
	private static defaultZoom: number = 0;
	private static zoomIntervals: number = 10;
	private static zoomFocalPointEdgeSnapThreshold: number = 200; // The threshold for making the focal point of a zoom event snap to the edge of the timeline to assist with zooming to items at the edge of the timeline

	chopModeButtonRef: any;
	marqueeButtonRef: any;
	private timelineContainerRef: any;
	private deleteButtonRef: any;
	private duplicateButtonRef: any;
	private playButtonRef: any;
	private rewindButtonRef: any;
	private stopButtonRef: any;
	private undoButtonRef: any;
	private redoButtonRef: any;
	private draggingItemsXDistance: number;
	private itemAction: string;
	private itemActionLeaderId: string;
	private leftMouseDown: boolean;
	private leftMouseDrag: boolean;
	private rightMouseDown: boolean;
	private rightMouseDrag: boolean;
	private isMarkerDragging: boolean;
	private markerDragged: boolean;
	private markerDragX: any;
	private markerDragPointerId: number | null = null;
	private contextMenuCloseTimer: any; // TODO: Complete context menu design and implementation
	private deleteGhostsTimeout: number | undefined;
	private nextprojectHistoryState: Project;
	private cursorMarkerRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
	private playheadMarkerRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
	private resizeObserver: ResizeObserver | null = null;
	private firstVisibleMeasure: number = 0;
	private lastVisibleMeasure: number = Timeline.numberOfMeasures;
	private preventLeftClick: boolean = false;
	private playheadLatched = false;
	private playheadShouldUnlatch = false;
	private touchPanPointerId: number | null = null;
	private touchPanStartX: number = 0;
	private touchPanStartY: number = 0;
	private touchPanScrollLeft: number = 0;
	private touchPanScrollTop: number = 0;
	private activeTouchPoints: Map<number, { clientX: number; clientY: number }> = new Map();
	private pinchStartDistance: number | null = null;
	private pinchStartZoom: number = 0;
	private pinchStartCenterX: number = 0;
	private pinchStartCenterY: number = 0;
	private pinchPrevCenterX: number = 0;
	private pinchPrevCenterY: number = 0;
	private marqueePointerId: number | null = null;
	private lastGhostLayoutSignature: string | null = null;

	constructor(props: TimelineProps) {
		super(props);
		this.state = {
			trackHeight: Timeline.minTrackHeight,
			timelineHeight: this.timelineContainerRef?.current?.Height || 0,
			measureWidth: Timeline.minMeasureWidth + (Timeline.maxMeasureWidth - Timeline.minMeasureWidth) * (Timeline.defaultZoom / Timeline.zoomIntervals),
			isDragging: false,
			startX: 0,
			startY: 0,
			scrollLeft: 0,
			scrollTop: 0,
			selectedItemIds: [],
			contextMenuVisible: false,
			contextMenuX: 0,
			contextMenuY: 0,
			contextMenuFadeOut: false,
			cursorMarkerMeasure: 0,
			isPlaying: false,
			playingMeasureNumber: 0,
			playbackLoading: false,
			chopMode: false,
			projectHistory: [props.project],
			projectHistoryFuture: [],
			isMarqueeToggled: false,
			isMarqueeActive: false,
			marqueeStartX: 0,
			marqueeStartY: 0,
			marqueeEndX: 0,
			marqueeEndY: 0,
			isZooming: false,
			isControlHeld: false,
			isShiftHeld: false,
			notationView: false,
			chordProgressionsByMeasure: new Map<number, { selection: BeatMappedChordProgression | undefined, options: BeatMappedChordProgression[] }>()
		};
		this.timelineContainerRef = React.createRef();
		this.deleteButtonRef = React.createRef();
		this.duplicateButtonRef = React.createRef();
		this.playButtonRef = React.createRef();
		this.rewindButtonRef = React.createRef();
		this.stopButtonRef = React.createRef();
		this.undoButtonRef = React.createRef();
		this.redoButtonRef = React.createRef();
		this.chopModeButtonRef = React.createRef();
		this.marqueeButtonRef = React.createRef();
		this.draggingItemsXDistance = 0;
		this.itemAction = '';
		this.itemActionLeaderId = '';
		this.leftMouseDown = false;
		this.leftMouseDrag = false;
		this.rightMouseDown = false;
		this.rightMouseDrag = false;
		this.isMarkerDragging = false;
		this.markerDragged = false;
		this.markerDragX = 0;
		this.nextprojectHistoryState = this.props.project.createCopy(true);
		this.cursorMarkerRef = React.createRef();
	}

	preventDefault = (event: Event) => {
		event.preventDefault();
	}

	componentDidUpdate(prevProps: TimelineProps) {
		if (!this.props.project.isSameProject(prevProps.project)) {
			// Clear the timeline history when the project changes
			this.setState({
				projectHistory: [],
				projectHistoryFuture: []
			});
		}

		if (this.props.deviceLayout.isPhoneOrSmaller && !prevProps.deviceLayout.isPhoneOrSmaller) {
			Timeline.measureMarkerHeight = 22
			Timeline.minMeasureWidth = 16;
			Timeline.trackLabelWidth = 60;
			this.updateZoom(0, 1, 1); // Reset zoom to minimum on phone layouts
		} else if (this.props.deviceLayout.isTabletOrSmaller && (!prevProps.deviceLayout.isTabletOrSmaller) || (!this.props.deviceLayout.isPhoneOrSmaller && prevProps.deviceLayout.isPhoneOrSmaller)) {
			Timeline.measureMarkerHeight = 26;
			Timeline.minMeasureWidth = 22;
			Timeline.trackLabelWidth = 80;
			this.updateZoom(0, 1, 1); // Reset zoom to minimum on phone layouts
		} else if (!this.props.deviceLayout.isTabletOrSmaller && prevProps.deviceLayout.isTabletOrSmaller) {
			Timeline.measureMarkerHeight = 30;
			Timeline.minMeasureWidth = 25;
			Timeline.trackLabelWidth = 100;
			this.updateZoom(0, 1, 1); // Reset zoom to minimum on phone layouts
		}

		this.reportGhostItemLayouts();
	}

	componentDidMount() {
		const container = this.timelineContainerRef.current;
		// Add wheel scroll event listener with passive set to false
		// Normally, we want to add event listeners in the JSX, but we need to set passive to false to prevent the default behavior of the wheel event
		container.addEventListener('wheel', this.handleTimelineZoom, { passive: false });
		// Add event listeners for hotkeys
		document.addEventListener('keydown', this.handleKeyDown);
		document.addEventListener('keyup', this.handleKeyUp);

		// Create observer for the timeline container to detect changes in size and scroll position
		if (this.timelineContainerRef.current) {
			this.resizeObserver = new ResizeObserver((entries) => {
				for (let entry of entries) {
					const newHeight = entry.target.clientHeight;
					if (newHeight !== this.state.timelineHeight) {
						this.setState({
							timelineHeight: newHeight
						});
					}
				}
			});
			this.resizeObserver.observe(this.timelineContainerRef.current);
		}

		this.reportGhostItemLayouts();
	}

	componentWillUnmount() {
		const container = this.timelineContainerRef.current;
		container.removeEventListener('wheel', this.handleTimelineZoom, { passive: false });
		document.removeEventListener('keydown', this.handleKeyDown);
		document.removeEventListener('keyup', this.handleKeyUp);

		if (this.resizeObserver && this.timelineContainerRef.current) {
			this.resizeObserver.unobserve(this.timelineContainerRef.current);
		}

		if (this.props.onGhostItemLayoutChange) {
			this.props.onGhostItemLayoutChange([]);
		}
	}

	handleKeyDown = (event: KeyboardEvent) => {
		event.preventDefault();

		if (this.props.beginnerTourActive) {
			// Don't let hotkeys mess up the tour
			return;
		}

		const key = event.key.toLowerCase(); // in case caps lock is on and also simplifies shift hotkeys
		if (key === 'control') {
			if (!this.state.chopMode) {
				this.setState({
					chopMode: true,
					isMarqueeToggled: false,
				});
			}
			this.setState({ isControlHeld: true });
		} else if (key === 'shift') {
			if (!this.state.isMarqueeToggled) {
				this.setState({
					isMarqueeToggled: true,
					chopMode: false,
				});
			};
			this.setState({ isShiftHeld: true });
		} else if (key == 'backspace' || key == 'delete') {
			this.deleteButtonRef?.current?.click();
		} else if (key == 'r') {
			this.rewindButtonRef?.current?.click();
		} else if (key == 's') {
			this.marqueeButtonRef?.current?.click();
		} else if (key == 'c') {
			this.chopModeButtonRef?.current?.click();
		} else if (key == 'd') {
			this.duplicateButtonRef?.current?.click();
		} else if (key == 'escape') {
			this.setState({
				selectedItemIds: [],
				isMarqueeActive: false
			});
		} else if (event.ctrlKey) {
			if ((event.shiftKey && key === 'z') || key === 'y') {
				this.redoButtonRef?.current?.click();
			} else if (key === 'z') {
				this.undoButtonRef?.current?.click();
			} else if (key === 'a') {
				// Select all
				this.setState({ selectedItemIds: this.getItems().map(item => item.id) });
			}
		} else if (key == ' ') {
			if (this.state.isPlaying) {
				this.stopButtonRef?.current?.click();
			} else {
				this.playButtonRef?.current?.click();
			}
		}
	}

	handleKeyUp = (event: KeyboardEvent) => {
		if (event.key === 'Shift') {
			if (this.state.isMarqueeActive) {
				const syntheticEvent = new MouseEvent('mouseup', { button: 0 });
				this.handleMouseUp(syntheticEvent as unknown as React.MouseEvent<Element, MouseEvent>); // syntheticEvent must be 'cast' as unknown and then as React.MouseEvent because synthetic event is using a constructor for a native mouse event which differs in construction slightly from the expected React.MouseEvent and would fail the typescript check
			}
			this.setState({ isShiftHeld: false });
		}
		if (event.key === 'Control' && this.state.chopMode) {
			this.setState({
				chopMode: false
			});
			this.setState({ isControlHeld: false });
		}
		if (event.key === 'Shift' && this.state.isMarqueeToggled) {
			this.setState({
				isMarqueeToggled: false
			});
		}
	}

	private getItems(trackId: string | null = null): ProjectTrackItem[] {
		return this.props.project.tracks.filter(track => trackId == null || track.id == trackId).flatMap((track) => track.items);
	}

	private lastAddedMeasure?: number;
	private lastAddedSections?: Section[];
	addItems = async (measure: number, sections: Section[]) => {
		// Snap to every four measures if the zoom level is less than 3
		if (this.calculateCurrentZoom() < 3) {
			measure = Math.floor(measure / 4) * 4;
		}

		// Prevent adding the same items multiple times
		if (this.lastAddedMeasure === measure && this.lastAddedSections === sections) return;
		this.lastAddedMeasure = measure;
		this.lastAddedSections = sections

		const project = this.props.project.createCopy(true);
		project.createNewItems(measure, sections);

		this.props.updateProject(project);
	}

	getProjectWithoutGhosts = (save: boolean) => {
		const project = this.props.project.createCopy(true);

		if (save) {
			this.props.updateProject(project);
		}

		return project;
	}

	makeGhostTracksAndItemsReal = () => {
		const project = this.props.project.createCopy();
		const newItems = project.makeGhostsReal();
		project.updateChordReductions();

		this.setStateAndUpdateProjectHistory({
			...this.state,
			selectedItemIds: newItems.map(item => item.id)
		}, project);
	}

	chopItem = (event: { pageX: number; }, id: string) => {
		const cursorX = event.pageX - this.timelineContainerRef.current.offsetLeft - Timeline.trackLabelWidth + this.timelineContainerRef.current.scrollLeft;
		const measure = Math.round(cursorX / this.state.measureWidth); // Calculate the measure based on the cursor's x position

		const project = this.props.project.createCopy();
		project.chopItem(id, measure);

		this.setStateAndUpdateProjectHistory({
			...this.state,
			selectedItemIds: [],
			chopMode: false
		}, project);
	}

	handleMouseDown: MouseEventHandler = (event) => {
		const timelineContainer = this.timelineContainerRef.current;
		if (!this.leftMouseDown && !this.rightMouseDown && event.button === 0) {
			// Left click
			this.leftMouseDown = true;
			this.leftMouseDrag = false;

			if (this.state.isMarqueeToggled) {
				this.beginMarqueeDrag(event.clientX, event.clientY);
			} else if (!this.state.isMarqueeToggled) {
				this.setState({
					isDragging: true,
					startX: event.pageX - timelineContainer.offsetLeft,
					startY: event.pageY - timelineContainer.offsetTop,
					scrollLeft: timelineContainer.scrollLeft,
					scrollTop: timelineContainer.scrollTop
				});
			}
			if (!this.state.chopMode && !this.state.isMarqueeToggled) timelineContainer.style.cursor = 'grabbing';
		} else if (!this.leftMouseDown && !this.rightMouseDown && event.button === 2) {
			// Right click
			this.rightMouseDown = true;
			this.rightMouseDrag = false;

			this.beginMarqueeDrag(event.clientX, event.clientY);

			if (!this.state.chopMode) timelineContainer.style.cursor = 'crosshair';
		}
	}

	handleMouseMove: MouseEventHandler = (event) => {
		const { draggingSections } = this.props;
		if (draggingSections) {
			return;
		}

		if (this.rightMouseDown) {
			this.rightMouseDrag = true;
		}

		if (this.rightMouseDrag || this.state.isMarqueeToggled) {
			if (this.state.isMarqueeActive) {
				this.updateMarqueeDrag(event.clientX, event.clientY);
			}
		}

		if (this.leftMouseDown && !this.leftMouseDrag) {
			this.leftMouseDrag = true;
			this.preventLeftClick = true;
		}
		if (this.leftMouseDrag && !this.state.isMarqueeToggled) { // TODO: Fix annoying bug with dragging being interrupted by random dragging
			event.preventDefault();
			const timelineContainer = this.timelineContainerRef.current;
			const { startX, startY, scrollLeft, scrollTop } = this.state;
			const x = event.pageX - timelineContainer.offsetLeft;
			const y = event.pageY - timelineContainer.offsetTop;
			const walkX = (x - startX);
			const walkY = (y - startY);
			timelineContainer.scrollLeft = scrollLeft - walkX;
			timelineContainer.scrollTop = scrollTop - walkY;
			// Unlatch when the timeline is scrolled
			if (this.playheadLatched) {
				this.playheadShouldUnlatch = true;
			}
		}
	}

	handleMouseUp: MouseEventHandler = (event) => {
		const timelineContainer = this.timelineContainerRef.current;
		if (this.leftMouseDown && event.button === 0) {
			if (this.state.isMarqueeActive) this.endMarqueeDrag(true);
			// Left click
			this.leftMouseDown = false;
			this.leftMouseDrag = false;
			setTimeout(() => { this.preventLeftClick = false; }, 100); // Set a delay before click events can be triggered to stop the drag end from acting as a click
			if (!this.state.chopMode && !this.state.isMarqueeToggled) timelineContainer.style.cursor = 'grab';
		} else if (this.rightMouseDown && event.button === 2) {
			// Right click
			if (this.state.isMarqueeActive) this.endMarqueeDrag(true);
			this.rightMouseDown = false;
			this.rightMouseDrag = false;
			if (!this.state.chopMode) timelineContainer.style.cursor = 'grab';
		}
	}

	handleMouseLeave = () => {
		const timelineContainer = this.timelineContainerRef.current;
		this.leftMouseDown = false;
		this.leftMouseDrag = false;
		this.rightMouseDown = false;
		this.rightMouseDrag = false;
		if (!this.state.chopMode && !this.state.isMarqueeToggled) timelineContainer.style.cursor = 'grab';
		if (this.state.isDragging) this.setState({ isDragging: false });
		if (this.state.isMarqueeActive && this.marqueePointerId === null) {
			this.calculateMarqueeSelection(true);
		}
	}

	private isTouchLikePointer(pointerType: string) {
		return pointerType === 'touch' || pointerType === 'pen';
	}

	public isPointWithinTimeline = (clientX: number, clientY: number) => {
		const timelineContainer = this.timelineContainerRef.current;
		if (!timelineContainer) {
			return false;
		}
		const rect = timelineContainer.getBoundingClientRect();
		return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
	};

	public getTimelineBoundingLeft = () => {
		const timelineContainer = this.timelineContainerRef.current;
		if (!timelineContainer) {
			return 0;
		}
		const rect = timelineContainer.getBoundingClientRect();
		return rect.left - timelineContainer.scrollLeft + Timeline.trackLabelWidth;
	};

	public previewLibrarySectionDropAtClientX = (clientX: number) => {
		this.previewLibrarySectionDrop(clientX);
	};

	public commitLibrarySectionDrop = () => {
		this.completeLibrarySectionDrop();
	};

	public cancelLibrarySectionPreview = () => {
		this.scheduleGhostCleanup();
	};

	private previewLibrarySectionDrop(clientX: number) {
		const { draggingSections } = this.props;
		const timelineContainer = this.timelineContainerRef.current;
		if (!draggingSections || !timelineContainer) {
			return;
		}

		window.clearTimeout(this.deleteGhostsTimeout);
		const offsetX = clientX - timelineContainer.getBoundingClientRect().left - Timeline.trackLabelWidth + timelineContainer.scrollLeft;
		const cursorMeasure = Math.floor(offsetX / this.state.measureWidth);
		this.addItems(cursorMeasure, draggingSections);
	}

	private scheduleGhostCleanup() {
		window.clearTimeout(this.deleteGhostsTimeout);
		this.deleteGhostsTimeout = window.setTimeout(() => {
			this.lastAddedMeasure = undefined;
			this.props.updateProject(this.props.project.createCopy(true));
		}, 500);
	}

	private completeLibrarySectionDrop() {
		if (!this.props.draggingSections) {
			return;
		}

		window.clearTimeout(this.deleteGhostsTimeout);
		this.deleteGhostsTimeout = undefined;

		TimelineSectionDropCompletedEvent.fire();
		this.makeGhostTracksAndItemsReal();
		this.props.setDraggingSections(null);
	}

	private resetTouchPanAndZoomState() {
		this.touchPanPointerId = null;
		this.touchPanStartX = 0;
		this.touchPanStartY = 0;
		this.touchPanScrollLeft = 0;
		this.touchPanScrollTop = 0;

		this.activeTouchPoints.clear();
		this.pinchStartDistance = null;
		this.pinchStartZoom = 0;
		this.pinchStartCenterX = 0;
		this.pinchStartCenterY = 0;
		this.pinchPrevCenterX = 0;
		this.pinchPrevCenterY = 0;

		if (this.state.isZooming) {
			this.setState({ isZooming: false });
		}

		this.marqueePointerId = null;

		if (this.isMarkerDragging) {
			this.isMarkerDragging = false;
			this.markerDragged = false;
			this.markerDragPointerId = null;
		}

		if (!this.state.chopMode && !this.state.isMarqueeToggled && this.timelineContainerRef?.current) {
			this.timelineContainerRef.current.style.cursor = 'grab';
		}
	}

	private releaseTouchPointerCapture(event: React.PointerEvent<HTMLDivElement>) {
		if (this.touchPanPointerId !== event.pointerId) {
			return;
		}

		if (event.currentTarget.hasPointerCapture && event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}

		this.resetTouchPanAndZoomState();
	}

	private handlePointerDownTimeline = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!this.isTouchLikePointer(event.pointerType) || this.props.draggingSections) {
			return;
		}

		if (!this.timelineContainerRef.current) {
			return;
		}

		const container = this.timelineContainerRef.current;

		if (
			this.touchPanPointerId !== null &&
			container.hasPointerCapture &&
			!container.hasPointerCapture(this.touchPanPointerId)
		) {
			this.resetTouchPanAndZoomState();
		}

		// Track active touch points for pinch gestures
		this.activeTouchPoints.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });

		if (this.activeTouchPoints.size === 2) {
			// Start pinch-zoom / two-finger pan
			if (this.touchPanPointerId !== null && event.currentTarget.hasPointerCapture && event.currentTarget.hasPointerCapture(this.touchPanPointerId)) {
				event.currentTarget.releasePointerCapture(this.touchPanPointerId);
			}
			this.touchPanPointerId = null;
			const points = Array.from(this.activeTouchPoints.values());
			const [p1, p2] = points;
			const distance = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
			const centerX = (p1.clientX + p2.clientX) / 2;
			const centerY = (p1.clientY + p2.clientY) / 2;
			this.pinchStartDistance = distance;
			this.pinchStartZoom = this.calculateCurrentZoom();
			this.pinchStartCenterX = centerX;
			this.pinchStartCenterY = centerY;
			this.pinchPrevCenterX = centerX;
			this.pinchPrevCenterY = centerY;
			this.setState({ isZooming: true });
			event.preventDefault();
			return;
		}

		if (this.activeTouchPoints.size > 2) {
			event.preventDefault();
			return;
		}

		// Single-finger behavior
		if (this.state.isMarqueeToggled) {
			if (this.marqueePointerId !== null) {
				return;
			}

			this.marqueePointerId = event.pointerId;
			this.beginMarqueeDrag(event.clientX, event.clientY);

			if (event.currentTarget.setPointerCapture) {
				event.currentTarget.setPointerCapture(event.pointerId);
			}

			event.preventDefault();
			return;
		}

		if (this.touchPanPointerId !== null) {
			return;
		}

		this.touchPanPointerId = event.pointerId;
		this.touchPanStartX = event.clientX;
		this.touchPanStartY = event.clientY;
		this.touchPanScrollLeft = this.timelineContainerRef.current.scrollLeft;
		this.touchPanScrollTop = this.timelineContainerRef.current.scrollTop;

		if (event.currentTarget.setPointerCapture) {
			event.currentTarget.setPointerCapture(event.pointerId);
		}

		this.timelineContainerRef.current.style.cursor = 'grabbing';
		event.preventDefault();
	};

	private handlePointerMoveTimeline = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!this.isTouchLikePointer(event.pointerType)) {
			return;
		}

		if (this.props.draggingSections) {
			return;
		}

		// Update tracked touch point
		if (this.activeTouchPoints.has(event.pointerId)) {
			this.activeTouchPoints.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });
		}

		if (this.activeTouchPoints.size >= 2) {
			const points = Array.from(this.activeTouchPoints.values());
			const [p1, p2] = points;
			const currentDistance = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
			const currentCenterX = (p1.clientX + p2.clientX) / 2;
			const currentCenterY = (p1.clientY + p2.clientY) / 2;

			if (this.pinchStartDistance !== null && this.timelineContainerRef.current) {
				event.preventDefault();

				// Pinch zoom
				const scale = currentDistance / (this.pinchStartDistance || currentDistance);
				let targetZoom = this.pinchStartZoom + (scale - 1) * 3;
				if (Math.abs(this.calculateCurrentZoom() - targetZoom) <= 1) {
					return; // Avoid excessive zoom updates for small changes
				}
				targetZoom = Math.round(targetZoom);
				targetZoom = Math.max(0, Math.min(Timeline.zoomIntervals - 1, targetZoom));
				this.updateZoom(targetZoom, this.pinchStartCenterX, this.pinchStartCenterY);

				// Two-finger pan using center movement
				const deltaCenterX = currentCenterX - this.pinchPrevCenterX;
				const deltaCenterY = currentCenterY - this.pinchPrevCenterY;
				this.pinchPrevCenterX = currentCenterX;
				this.pinchPrevCenterY = currentCenterY;
				this.timelineContainerRef.current.scrollLeft -= deltaCenterX;
				this.timelineContainerRef.current.scrollTop -= deltaCenterY;
				if (this.playheadLatched) {
					this.playheadShouldUnlatch = true;
				}
			}
			return;
		}

		if (this.marqueePointerId === event.pointerId && this.state.isMarqueeToggled) {
			this.updateMarqueeDrag(event.clientX, event.clientY);
			event.preventDefault();
			return;
		}

		// If a touch/pen pointer is dragging the cursor marker, move it instead of panning.
		if (this.isMarkerDragging && this.markerDragPointerId === event.pointerId) {
			if (this.timelineContainerRef.current) {
				event.preventDefault();
				const containerRect = this.timelineContainerRef.current.getBoundingClientRect();
				this.markerDragged = true;
				this.markerDragX =
					event.clientX -
					containerRect.left +
					this.timelineContainerRef.current.scrollLeft -
					Timeline.trackLabelWidth;

				if (this.cursorMarkerRef.current) {
					this.cursorMarkerRef.current.style.left = `${this.markerDragX}px`;
				}
			}
			return;
		}

		if (this.touchPanPointerId === event.pointerId && this.timelineContainerRef.current) {
			event.preventDefault();
			const timelineContainer = this.timelineContainerRef.current;
			const deltaX = event.clientX - this.touchPanStartX;
			const deltaY = event.clientY - this.touchPanStartY;
			timelineContainer.scrollLeft = this.touchPanScrollLeft - deltaX;
			timelineContainer.scrollTop = this.touchPanScrollTop - deltaY;
			if (this.playheadLatched) {
				this.playheadShouldUnlatch = true;
			}
			this.reportGhostItemLayouts();
		}
	};

	private handlePointerUpTimeline = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!this.isTouchLikePointer(event.pointerType)) {
			return;
		}

		if (this.activeTouchPoints.has(event.pointerId)) {
			this.activeTouchPoints.delete(event.pointerId);
		}

		if (this.activeTouchPoints.size < 2 && this.state.isZooming) {
			this.pinchStartDistance = null;
			this.pinchStartZoom = 0;
			this.pinchStartCenterX = 0;
			this.pinchStartCenterY = 0;
			this.setState({ isZooming: false });
		}

		// Finish marquee selection if this pointer was dragging it
		if (this.marqueePointerId === event.pointerId) {
			this.endMarqueeDrag(true);
			this.marqueePointerId = null;

			if (event.currentTarget.hasPointerCapture && event.currentTarget.hasPointerCapture(event.pointerId)) {
				event.currentTarget.releasePointerCapture(event.pointerId);
			}
			event.preventDefault();
			return;
		}

		// Finish touch/pen marker drag
		if (this.isMarkerDragging && this.markerDragPointerId === event.pointerId) {
			event.preventDefault();
			if (this.markerDragged) {
				this.snapMarkerToClosestMeasure();
			}
			this.isMarkerDragging = false;
			this.markerDragged = false;
			this.markerDragPointerId = null;
			return;
		}

		if (this.touchPanPointerId === event.pointerId) {
			this.releaseTouchPointerCapture(event);
		}

		if (this.props.draggingSections) {
			event.preventDefault();
			return;
		}
	};

	private handlePointerCancelTimeline = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!this.isTouchLikePointer(event.pointerType)) {
			return;
		}

		if (this.activeTouchPoints.has(event.pointerId)) {
			this.activeTouchPoints.delete(event.pointerId);
		}

		if (this.activeTouchPoints.size < 2 && this.state.isZooming) {
			this.pinchStartDistance = null;
			this.pinchStartZoom = 0;
			this.pinchStartCenterX = 0;
			this.pinchStartCenterY = 0;
			this.setState({ isZooming: false });
		}

		if (this.marqueePointerId === event.pointerId) {
			this.endMarqueeDrag(true);
			this.marqueePointerId = null;
			event.preventDefault();
			return;
		}

		if (this.isMarkerDragging && this.markerDragPointerId === event.pointerId) {
			this.isMarkerDragging = false;
			this.markerDragged = false;
			this.markerDragPointerId = null;
			return;
		}

		if (this.touchPanPointerId === event.pointerId) {
			this.releaseTouchPointerCapture(event);
		}

		if (this.props.draggingSections) {
			return;
		}
	};

	calculateMarqueeSelection = (removeMarquee: boolean) => {
		const { marqueeStartX, marqueeStartY, marqueeEndX, marqueeEndY, trackHeight } = this.state;
		const tracks = this.props.project.tracks;

		const timelineContainer = this.timelineContainerRef.current;
		const startX = Math.min(marqueeStartX, marqueeEndX) - timelineContainer.getBoundingClientRect().left - Timeline.trackLabelWidth + timelineContainer.scrollLeft;
		const startY = Math.min(marqueeStartY, marqueeEndY) - timelineContainer.getBoundingClientRect().top - Timeline.measureMarkerHeight + timelineContainer.scrollTop;
		const endX = Math.max(marqueeStartX, marqueeEndX) - timelineContainer.getBoundingClientRect().left - Timeline.trackLabelWidth + timelineContainer.scrollLeft;
		const endY = Math.max(marqueeStartY, marqueeEndY) - timelineContainer.getBoundingClientRect().top - Timeline.measureMarkerHeight + timelineContainer.scrollTop;
		const selectedItems: string[] = [];

		tracks.forEach((track, index) => {
			track.items.forEach((item) => {
				const itemStartX = (item.getStartMeasure() + item.getTrimStart()) * this.state.measureWidth;
				const itemEndX = (item.getStartMeasure() + item.getSection().getLength() - item.getTrimEnd()) * this.state.measureWidth;
				const itemStartY = index * trackHeight;
				const itemEndY = (index + 1) * trackHeight;

				if (startX < itemEndX && endX > itemStartX && startY < itemEndY && endY > itemStartY) {
					selectedItems.push(item.id);
				}
			});
		});

		this.setState((prevState) => ({
			selectedItemIds: prevState.isShiftHeld
				? prevState.selectedItemIds.concat(selectedItems)
				: selectedItems,
		}), () => {
			if (removeMarquee) {
				this.setState({
					isMarqueeActive: false,
					isMarqueeToggled: false,
					marqueeStartX: 0,
					marqueeStartY: 0,
					marqueeEndX: 0,
					marqueeEndY: 0,
				});
			}
		});
	};

	handleContextMenu: MouseEventHandler = (event) => {
		event.preventDefault(); // disable default context menu
	}

	handleContextMenuMouseEnter = () => {
		if (this.contextMenuCloseTimer) {
			clearTimeout(this.contextMenuCloseTimer);
		}
		if (this.state.contextMenuFadeOut) {
			this.setState({
				contextMenuVisible: true,
				contextMenuFadeOut: false
			});
		}
	}

	handleContextMenuMouseLeave = () => {
		if (this.contextMenuCloseTimer) {
			clearTimeout(this.contextMenuCloseTimer);
		}
		this.contextMenuCloseTimer = setTimeout(() => {
			this.setState({
				contextMenuFadeOut: true
			});
		}, 500);
	}

	handleContextMenuTransitionEnd = () => {
		if (this.state.contextMenuVisible) {
			this.setState({
				contextMenuVisible: false,
				contextMenuX: 0,
				contextMenuY: 0,
				contextMenuFadeOut: false
			});
		}
	}

	// TODO: make sure the page has no overflow so there is no page scroll when zooming
	handleTimelineZoom = (event: React.WheelEvent<HTMLDivElement>) => {
		if (event.ctrlKey) {
			event.preventDefault();
			event.stopPropagation();

			if (Math.abs(event.deltaY) > 1) {
				this.executeTimelineZoomEvent(event);
			}
		}
	};

	executeTimelineZoomEvent = Utils.throttle((
		event: React.WheelEvent<HTMLDivElement>
	) => {
		this.updateZoom(this.calculateCurrentZoom() - (event.deltaY) / 100, event.clientX, event.clientY);
	}, 20);

	updateZoom = (newZoom: number, focalPointX?: number, focalPointY?: number) => {
		let focalPointXRelativeToTimeline = (focalPointX ? focalPointX - this.timelineContainerRef.current.getBoundingClientRect().left : this.timelineContainerRef.current.getBoundingClientRect().width / 2) - Timeline.trackLabelWidth;
		let focalPointYRelativeToTimeline = focalPointY ? focalPointY - this.timelineContainerRef.current.getBoundingClientRect().top : this.timelineContainerRef.current.getBoundingClientRect().height / 2;
		const xHighSnapPoint = this.timelineContainerRef.current.getBoundingClientRect().width - Timeline.trackLabelWidth - Timeline.zoomFocalPointEdgeSnapThreshold;
		const yHighSnapPoint = this.timelineContainerRef.current.getBoundingClientRect().height - Timeline.zoomFocalPointEdgeSnapThreshold;
		focalPointXRelativeToTimeline = focalPointXRelativeToTimeline < Timeline.zoomFocalPointEdgeSnapThreshold ? 0 : focalPointXRelativeToTimeline > xHighSnapPoint ? this.timelineContainerRef.current.getBoundingClientRect().width - Timeline.trackLabelWidth : focalPointXRelativeToTimeline;
		focalPointYRelativeToTimeline = focalPointYRelativeToTimeline < Timeline.zoomFocalPointEdgeSnapThreshold ? 0 : focalPointYRelativeToTimeline > yHighSnapPoint ? this.timelineContainerRef.current.getBoundingClientRect().height : focalPointYRelativeToTimeline;

		const currentZoom = this.calculateCurrentZoom();
		const zoomingOut = newZoom < currentZoom
		newZoom = Math.round(newZoom); // Snap new zoom to intervals
		newZoom = newZoom == currentZoom ? zoomingOut ? newZoom - 1 : newZoom + 1 : newZoom; // Make sure the zoom always changes by at least 1
		newZoom = Math.max(0, Math.min(Timeline.zoomIntervals - 1, newZoom)); // Set limits
		const timelineContainer = this.timelineContainerRef.current;
		const timelineWidth = this.state.measureWidth * Timeline.numberOfMeasures;
		const focalPointXRatio = (timelineContainer.scrollLeft + focalPointXRelativeToTimeline) / timelineWidth;
		const focalPointYRatio = (timelineContainer.scrollTop + focalPointYRelativeToTimeline) / (this.state.trackHeight * this.props.project.tracks.length + Timeline.measureMarkerHeight);
		const measureWidth = Timeline.minMeasureWidth + (Timeline.maxMeasureWidth - Timeline.minMeasureWidth) * newZoom / Timeline.zoomIntervals;
		const notationViewZoomThreshold = Timeline.zoomIntervals - 1; // Temporary fix for notation view optimization problems
		const notationView = newZoom >= notationViewZoomThreshold;
		// Between the "start increasing track height" threshold and the "notation view" threshold, the track height will increase to make room for the notation view
		const tackHeightGradient = newZoom / notationViewZoomThreshold * (Timeline.notationViewTrackHeight - Timeline.minTrackHeight) + Timeline.minTrackHeight;
		if (this.playheadMarkerRef.current) this.playheadMarkerRef.current.style.visibility = 'hidden'; // Hide the play marker when zooming because it will move around in weird ways
		this.setState({
			measureWidth,
			notationView,
			trackHeight: notationView ? Timeline.notationViewTrackHeight : tackHeightGradient
		}, () => {
			// Scroll to the same ratio of the timeline as before to make sure the focal point stays in the same place
			timelineContainer.scrollLeft = focalPointXRatio * this.state.measureWidth * Timeline.numberOfMeasures - focalPointXRelativeToTimeline;
			timelineContainer.scrollTop = Math.min(focalPointYRatio * (this.state.trackHeight * this.props.project.tracks.length + Timeline.measureMarkerHeight) - focalPointYRelativeToTimeline, timelineContainer.scrollHeight - timelineContainer.clientHeight);

			this.sendVisibleTimelineMeasuresChangedEvent();

			// Sometimes when zooming out, the timeline height is less than the container height, so we need to scroll up to make sure the track labels stay aligned with the tracks and we don't get any empty space at the bottom
			const timelineHeightMinusScroll = this.state.trackHeight * this.props.project.tracks.length + Timeline.measureMarkerHeight - timelineContainer.scrollTop;
			if (zoomingOut && timelineHeightMinusScroll < timelineContainer.clientHeight) {
				timelineContainer.scrollTop = timelineContainer.scrollTop - (timelineContainer.clientHeight - timelineHeightMinusScroll);
			}

			// Update the cursor marker position
			if (this.cursorMarkerRef.current && !this.isMarkerDragging) {
				this.cursorMarkerRef.current.style.left = `${this.state.cursorMarkerMeasure * this.state.measureWidth}px`
			}
		});
	}

	handleTimelineScrollCapture = (event: React.WheelEvent<HTMLDivElement>) => {
		event.stopPropagation();
		event.preventDefault();
		this.sendVisibleTimelineMeasuresChangedEvent();

		// Move the cursor marker's top with the timeline container as it is scrolled.
		// This is to make sure we don't see the bottom of the cursor marker when scrolling vertically, which has been suprisingly annoying.
		const cursorMarker = this.cursorMarkerRef.current;
		if (cursorMarker && this.timelineContainerRef.current) {
			cursorMarker.style.top = `${this.timelineContainerRef.current.scrollTop}px`;
		}

		this.reportGhostItemLayouts();
	}

	sendVisibleTimelineMeasuresChangedEvent = () => {
		const timelineContainer = this.timelineContainerRef.current;
		let firstVisibleMeasure = 0;
		let lastVisibleMeasure = Timeline.numberOfMeasures;
		if (timelineContainer) {
			firstVisibleMeasure = Math.floor(timelineContainer.scrollLeft / this.state.measureWidth);
			lastVisibleMeasure = Math.ceil((timelineContainer.scrollLeft + timelineContainer.clientWidth) / this.state.measureWidth);
		}
		if (firstVisibleMeasure != this.firstVisibleMeasure || lastVisibleMeasure != this.lastVisibleMeasure) {
			VisibleTimelineMeasuresChangedEvent.fire({ firstVisibleMeasure, lastVisibleMeasure });
			this.firstVisibleMeasure = firstVisibleMeasure;
			this.lastVisibleMeasure = lastVisibleMeasure;
		}
	}

	private reportGhostItemLayouts = () => {
		const { onGhostItemLayoutChange, draggingSections, project } = this.props;
		if (!onGhostItemLayoutChange) {
			return;
		}

		const timelineContainer = this.timelineContainerRef.current;
		if (!timelineContainer) {
			return;
		}

		if (!draggingSections || draggingSections.length === 0) {
			if (this.lastGhostLayoutSignature !== 'none') {
				onGhostItemLayoutChange([]);
				this.lastGhostLayoutSignature = 'none';
			}
			return;
		}

		const containerRect = timelineContainer.getBoundingClientRect();
		const scrollLeft = timelineContainer.scrollLeft;
		const scrollTop = timelineContainer.scrollTop;
		const layouts: GhostItemLayout[] = [];

		project.tracks.forEach((track, trackIndex) => {
			track.items.forEach((item) => {
				if (!item.ghosted) {
					return;
				}
				const section = item.getSection();
				const sectionId = section.id;
				const dragToken = section.getDragToken ? section.getDragToken() : undefined;
				const matchId = dragToken ?? sectionId;
				const leftWithinTrack = (item.getStartMeasure() + item.getTrimStart()) * this.state.measureWidth;
				const topWithinTrack = trackIndex * this.state.trackHeight;
				const rect: ScreenRect = {
					left: containerRect.left + Timeline.trackLabelWidth - scrollLeft + leftWithinTrack,
					top: containerRect.top + Timeline.measureMarkerHeight - scrollTop + topWithinTrack,
					width: item.getTrimmedLength() * this.state.measureWidth,
					height: this.state.trackHeight
				};
				layouts.push({ sectionId, dragToken, matchId, rect });
			});
		});

		const signature = layouts.length === 0
			? 'empty'
			: JSON.stringify(layouts.map(layout => [
				layout.matchId,
				Number(layout.rect.left.toFixed(2)),
				Number(layout.rect.top.toFixed(2)),
				Number(layout.rect.width.toFixed(2)),
				Number(layout.rect.height.toFixed(2)),
			]));

		if (signature !== this.lastGhostLayoutSignature) {
			onGhostItemLayoutChange(layouts);
			this.lastGhostLayoutSignature = signature;
		}
	}

	calculateCurrentZoom = () => {
		return (this.state.measureWidth - Timeline.minMeasureWidth) / (Timeline.maxMeasureWidth - Timeline.minMeasureWidth) * Timeline.zoomIntervals;
	}

	private lastTargetMeasure: number | undefined;
	executeItemAction = Utils.throttle((
		cursorX: number,
		targetLeftX: number,
		finalizedMove: boolean
	) => {
		const { selectedItemIds } = this.state;
		const project = this.props.project.createCopy();

		const itemActionLeader = project.getItems().find((item) => item.id === this.itemActionLeaderId);
		if (!itemActionLeader) {
			console.error('Item action leader could not be found.')
			return;
		}

		if (this.itemAction === 'dragging') {
			// Calculate the measure based on the drop position
			const position = cursorX - targetLeftX - this.draggingItemsXDistance;
			const targetMeasure = Math.round(position / this.state.measureWidth) - itemActionLeader.getTrimStart();

			if (!finalizedMove && this.lastTargetMeasure !== undefined && this.lastTargetMeasure === targetMeasure) {
				return;
			}
			this.lastTargetMeasure = targetMeasure;

			const moveCompleted = project.moveItems(selectedItemIds, itemActionLeader, targetMeasure);
			if (!moveCompleted) {
				return;
			}

			if (finalizedMove) {
				project.updateChordReductions();
				this.lastTargetMeasure = undefined;
				this.setStateAndUpdateProjectHistory(this.state, project);

				if (this.props.beginnerTourActive && this.props.currentTourStep === 4) {
					this.props.moveToNextTourStep();
				}
			} else {
				this.props.updateProject(project);
			}
		} else if (this.itemAction === 'trimming-left') {
			// Calculate the measure based on the drop position
			const position = cursorX - targetLeftX;
			const targetMeasure = Math.round(position / this.state.measureWidth);

			if (!finalizedMove && this.lastTargetMeasure !== undefined && this.lastTargetMeasure === targetMeasure) {
				return;
			}
			this.lastTargetMeasure = targetMeasure;

			const trimCompleted = project.trimLeftItems(selectedItemIds, itemActionLeader, targetMeasure);
			if (!trimCompleted) {
				return;
			}

			if (finalizedMove) {
				project.updateChordReductions();
				this.lastTargetMeasure = undefined;
				this.setStateAndUpdateProjectHistory({
					...this.state
				}, project);
			} else {
				this.props.updateProject(project);
			}
		} else if (this.itemAction === 'trimming-right') {
			// Calculate the measure based on the drop position
			const position = cursorX - targetLeftX;
			var targetMeasure = Math.round(position / this.state.measureWidth);
			if (targetMeasure > Timeline.numberOfMeasures) {
				// Don't allow items to be dropped outside of the timeline
				targetMeasure = Timeline.numberOfMeasures - 1;
			}
			if (targetMeasure < 0) {
				targetMeasure = 0;
			}

			if (!finalizedMove && this.lastTargetMeasure !== undefined && this.lastTargetMeasure === targetMeasure) {
				return;
			}
			this.lastTargetMeasure = targetMeasure;

			const trimCompleted = project.trimRightItems(selectedItemIds, itemActionLeader, targetMeasure);
			if (!trimCompleted) {
				return;
			}

			if (finalizedMove) {
				project.updateChordReductions();
				this.lastTargetMeasure = undefined;
				this.setStateAndUpdateProjectHistory({
					...this.state
				}, project);
			} else {
				this.props.updateProject(project);
			}
		}
	}, 30);

	handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		event.stopPropagation();
		if (this.preventLeftClick) { // Prevent click event if a drag is happening
			event.preventDefault();
			return;
		}

		if (this.contextMenuCloseTimer) {
			clearTimeout(this.contextMenuCloseTimer);
		}

		this.setState({
			contextMenuFadeOut: true,
			selectedItemIds: []
		});
	}

	private beginMarqueeDrag(clientX: number, clientY: number) {
		this.setState({
			isMarqueeActive: true,
			marqueeStartX: clientX,
			marqueeStartY: clientY,
			marqueeEndX: clientX,
			marqueeEndY: clientY,
		});
	}

	private updateMarqueeDrag(clientX: number, clientY: number) {
		if (!this.state.isMarqueeActive) {
			return;
		}

		this.setState({
			marqueeEndX: clientX,
			marqueeEndY: clientY,
		}, () => {
			if (this.state.isMarqueeActive) {
				this.calculateMarqueeSelection(false);
			}
		});
	}

	private endMarqueeDrag(finalize: boolean) {
		if (!this.state.isMarqueeActive) {
			return;
		}

		if (finalize) {
			this.calculateMarqueeSelection(true);
		} else {
			this.setState({
				isMarqueeActive: false,
				isMarqueeToggled: false,
				marqueeStartX: 0,
				marqueeStartY: 0,
				marqueeEndX: 0,
				marqueeEndY: 0,
			});
		}
	}

	handleMeasureMarkerClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (this.preventLeftClick) { // Prevent click event if a drag is happening
			event.preventDefault();
			return;
		}

		this.markerDragX = event.pageX - event.currentTarget.getBoundingClientRect().left - Timeline.trackLabelWidth;
		this.snapMarkerToClosestMeasure();
	}

	handleMarkerMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		event.stopPropagation();
		this.isMarkerDragging = true;
	};

	private handleMarkerPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		// Prevent timeline touch handlers from initiating panning when grabbing the marker
		event.stopPropagation();

		if (!this.isTouchLikePointer(event.pointerType)) {
			this.handleMarkerMouseDown(event as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>);
			return;
		}

		this.isMarkerDragging = true;
		this.markerDragged = false;

		if (this.timelineContainerRef.current) {
			const containerRect = this.timelineContainerRef.current.getBoundingClientRect();
			this.markerDragX =
				event.clientX -
				containerRect.left +
				this.timelineContainerRef.current.scrollLeft -
				Timeline.trackLabelWidth;
		} else {
			this.markerDragX = 0;
		}

		this.markerDragPointerId = event.pointerId;

		if (event.currentTarget.setPointerCapture) {
			event.currentTarget.setPointerCapture(event.pointerId);
		}
	};

	handleMarkerMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (!this.isMarkerDragging) return;
		event.preventDefault();
		this.markerDragged = true;
		this.markerDragX = event.pageX - event.currentTarget.getBoundingClientRect().left + this.timelineContainerRef.current.scrollLeft - Timeline.trackLabelWidth;
		if (this.cursorMarkerRef.current) {
			this.cursorMarkerRef.current.style.left = `${this.markerDragX}px`
		}
	};

	handleMarkerMouseUp = () => {
		if (!this.isMarkerDragging) return;
		this.isMarkerDragging = false;
		if (!this.markerDragged) return;
		this.markerDragged = false;
		this.snapMarkerToClosestMeasure();
	};

	snapMarkerToClosestMeasure = () => {
		var closestMeasure = Math.round(this.markerDragX / this.state.measureWidth);
		closestMeasure = Math.min(Timeline.numberOfMeasures - 1, Math.max(0, closestMeasure));
		this.setState({ cursorMarkerMeasure: closestMeasure }, () => {
			if (this.cursorMarkerRef.current) {
				this.cursorMarkerRef.current.style.left = `${this.state.cursorMarkerMeasure * this.state.measureWidth}px`
			}
		});
	}

	startDraggingItems = (itemActionLeaderId: string, xDistance: number) => {
		this.itemAction = 'dragging';
		this.draggingItemsXDistance = xDistance;
		this.itemActionLeaderId = itemActionLeaderId;
	}

	startTrimmingItemsLeft = (itemActionLeaderId: string) => {
		this.itemAction = 'trimming-left';
		this.itemActionLeaderId = itemActionLeaderId;
	}

	startTrimmingItemsRight = (itemActionLeaderId: string) => {
		this.itemAction = 'trimming-right';
		this.itemActionLeaderId = itemActionLeaderId;
	}

	addItemToSelectedItems = (id: string) => {
		if (!this.state.selectedItemIds.includes(id)) {
			if (this.state.isShiftHeld) {
				this.setState((prevState) => ({
					selectedItemIds: [...prevState.selectedItemIds, id]
				}));
			} else {
				this.setState({
					selectedItemIds: [id]
				});
			}
		}
	}

	removeItemFromSelectedItems = (id: string) => {
		this.setState((prevState) => ({
			selectedItemIds: prevState.selectedItemIds.filter((itemId) => itemId !== id)
		}));
	}

	clearSelectedItems = () => {
		this.setState({
			selectedItemIds: []
		});
	}

	handleStopButtonClick = () => {
		if (this.state.isPlaying) {
			MidiPlayer.stop();
		}
		this.setState({ isPlaying: false }, () => {
			this.resetTouchPanAndZoomState();
			this.playheadLatched = false;
			this.playheadShouldUnlatch = false;
		});
	};

	handlePlayButtonClick = async () => {
		const { isPlaying, playbackLoading } = this.state;
		if (!isPlaying && !playbackLoading && this.props.project.tracks.length > 0) {
			// Set the cursor marker to the first measure with an item
			const firstMeasure = this.props.project.tracks.reduce((minMeasure, track) => {
				return Math.min(minMeasure, track.items.reduce((minMeasure, item) => {
					return Math.min(minMeasure, item.getStartMeasure() + item.getTrimStart());
				}, Infinity));
			}, Infinity);
			var newCursorMeasure = this.state.cursorMarkerMeasure;
			if (firstMeasure < Infinity && this.state.cursorMarkerMeasure < firstMeasure) {
				newCursorMeasure = firstMeasure;
			}

			this.setState({
				playbackLoading: true,
				cursorMarkerMeasure: newCursorMeasure
			}, async () => {
				const { cursorMarkerMeasure } = this.state;
				const project = this.props.project;
				const tracks = project.tracks;
				if (this.cursorMarkerRef.current) {
					this.cursorMarkerRef.current.style.left = `${cursorMarkerMeasure * this.state.measureWidth}px`
				}
				MidiPlayer.stop();
				let lastMeasure = 0;
				const playLoopCallback = async (measureNumber: number, currentMeasurePercentage: number) => {
					if (!this.state.isZooming) {
						const playPoint = this.state.measureWidth * measureNumber + (this.state.measureWidth * currentMeasurePercentage);
						if (this.playheadMarkerRef.current) {
							this.playheadMarkerRef.current.style.visibility = 'visible';
							this.playheadMarkerRef.current.style.left = `${playPoint}px`;
						}
						this.timelineContainerRef.current.scrollBehavior = '';
						if (!this.playheadShouldUnlatch && this.timelineContainerRef.current && MidiPlayer.getTrackCount() > 0 && !this.leftMouseDrag && !this.rightMouseDrag) {
							this.playheadLatched = true;
							const targetScroll = playPoint - this.state.measureWidth;
							if (lastMeasure !== measureNumber) {
								lastMeasure = measureNumber;
								if (this.timelineContainerRef.current.scrollLeft < targetScroll && this.props.deviceLayout.deviceLayout === DeviceLayout.Desktop) {
									this.timelineContainerRef.current.scroll({ left: targetScroll, behavior: 'smooth' })
								}
							}
						}
					}
				}
				const stopCallback = () => {
					this.setState({ isPlaying: false }, () => {
						this.resetTouchPanAndZoomState();
						this.playheadLatched = false;
						this.playheadShouldUnlatch = false;
					});
				}
				const removeTrackCallback = () => { }

				MidiPlayer.setCallbacks(playLoopCallback, stopCallback, removeTrackCallback);

				let shouldContinue = true;
				// Assemble each track into one midi measures that concatenates all the track's item stems
				await Promise.all(tracks.map(async (track) => {
					const midiPlayerTrack: MidiPlayerTrack = {
						trackId: track.id,
						instrumentName: track.getInstrument().soundfontInstrument as InstrumentName,
						measures: track.toMidiMeasures(),
						isPercussion: track.getInstrument().midiProgram === 0
					};
					shouldContinue = shouldContinue && await MidiPlayer.addTrack(midiPlayerTrack);
				}));

				if (!shouldContinue) {
					this.setState({ playbackLoading: false });
					return;
				}

				// Beginner tour
				if (this.props.beginnerTourActive && (this.props.currentTourStep === 5)) {
					this.props.moveToNextTourStep();
					const cursorPoint = this.state.measureWidth * this.state.cursorMarkerMeasure;
					if (this.state.notationView) {
						const targetScroll = cursorPoint - this.state.measureWidth;
						if (this.timelineContainerRef.current.scrollLeft < targetScroll) {
							this.timelineContainerRef.current.scroll({ left: targetScroll, behavior: 'smooth' })
						}
					} else {
						const targetScroll = cursorPoint - this.timelineContainerRef.current.clientWidth / 6;
						if (this.timelineContainerRef.current.scrollLeft < targetScroll) {
							this.timelineContainerRef.current.scrollLeft = targetScroll;
						}
					}
				}

				// Follow the playhead by default with these settings
				this.playheadLatched = false;
				this.playheadShouldUnlatch = false;

				MidiPlayer.play(cursorMarkerMeasure);
				if (this.playheadMarkerRef.current && this.cursorMarkerRef.current) {
					this.playheadMarkerRef.current.style.visibility = 'hidden';
					this.playheadMarkerRef.current.style.left = this.cursorMarkerRef.current.style.left;
				}
				this.setState({
					isPlaying: true,
					playingMeasureNumber: cursorMarkerMeasure,
					playbackLoading: false
				});
			});
		}
	};

	handleRewindButtonClick = () => {
		if (this.state.isPlaying) {
			MidiPlayer.stop();
		}
		this.setState({
			cursorMarkerMeasure: 0
		}, () => {
			if (this.cursorMarkerRef.current) {
				this.cursorMarkerRef.current.style.left = `${this.state.cursorMarkerMeasure * this.state.measureWidth}px`
			}
			this.timelineContainerRef.current.scroll({ left: 0, behavior: 'smooth' })
		});
	};

	handleMarqueeButtonClick = () => {
		this.setState((prevState) => ({
			isMarqueeToggled: !prevState.isMarqueeToggled
		}), () => {
			if (!this.state.isMarqueeToggled) {
				const timelineContainer = this.timelineContainerRef.current;
				timelineContainer.style.cursor = 'grab';
			};
			if (this.state.isMarqueeToggled) { this.setState({ chopMode: false }) };
		});
	};

	handleChopModeButtonClick = () => {
		this.setState((prevState) => ({
			chopMode: !prevState.chopMode
		}), () => {
			if (this.state.chopMode) {
				this.setState({ isMarqueeToggled: false })
			} else {
				const timelineContainer = this.timelineContainerRef.current;
				timelineContainer.style.cursor = 'grab';
			}
		});
	};

	handleDeleteButtonClick = () => {
		const { selectedItemIds } = this.state;
		if (selectedItemIds?.length <= 0) return;
		const project = this.props.project.createCopy();
		project.deleteItems(selectedItemIds);
		this.setStateAndUpdateProjectHistory({
			...this.state,
			selectedItemIds: []
		}, project);
	}

	handleDuplicateButtonClick = () => {
		const { selectedItemIds } = this.state;
		if (selectedItemIds?.length <= 0) return;

		const project = this.props.project.createCopy();
		const items = project.getItems().filter(item => selectedItemIds.includes(item.id));

		const earliestStart = items.reduce((minMeasure, item) => {
			return Math.min(minMeasure, item.getStartMeasure() + item.getTrimStart());
		}, Infinity);

		const itemIdToRelativeMeasure = new Map<string, number>
		items.forEach(item => {
			itemIdToRelativeMeasure.set(item.id, item.getStartMeasure() - earliestStart + item.getTrimStart());
		});

		const latestEnd = items.reduce((maxMeasure, item) => {
			return Math.max(maxMeasure, item.getStartMeasure() + item.getSection().getLength() - item.getTrimEnd());
		}, 0);

		const measure = latestEnd;
		const newItems = project.duplicateItems(items, itemIdToRelativeMeasure, measure);

		if (newItems.length > 0) {
			this.setStateAndUpdateProjectHistory({
				...this.state,
				selectedItemIds: newItems.map(item => item.id), // make the newly copied items selected
				chopMode: false // turn cut mode off by default when adding items
			}, project);
		}
	}

	handleTempoChangeCreation = (measure: number, tempo: Tempo) => {
		const project = this.props.project.createCopy();
		project.addTempoChange(measure, tempo);
		this.setStateAndUpdateProjectHistory(this.state, project);
	}

	setStateAndUpdateProjectHistory = (newState: TimelineState, project: Project | null = null) => {
		const newProjectHistory = [...this.state.projectHistory, this.nextprojectHistoryState];
		if (this.state.projectHistory.length > Timeline.projectHistoryDepth) {
			newProjectHistory.shift();
		}
		if (project) {
			this.props.updateProject(project, () => {
				// Get the chord progression candidate list from the project
				const chordProgressionOptionsLists = project.getDetectedChordProgressions();
				// Create a map of chord progressions candidates by measure for the state, taking the current selection if we have one and it is still an option
				const newChordProgressionsByMeasure: Map<number, { selection: BeatMappedChordProgression | undefined, options: BeatMappedChordProgression[] }> = new Map();
				for (const chordProgressionOption of chordProgressionOptionsLists) {
					const measure = chordProgressionOption.measureNumber;
					const chords = chordProgressionOption.chordProgressions;
					let selection: BeatMappedChordProgression | undefined = undefined;
					const previousMapping = this.state.chordProgressionsByMeasure.get(measure);
					if (previousMapping !== undefined) {
						const previousSelection = previousMapping.selection;
						if (previousSelection && chords.includes(previousSelection)) {
							selection = previousSelection;
						}
					}
					newChordProgressionsByMeasure.set(measure, {
						selection,
						options: chords
					});
				}
				this.setState({
					...newState,
					projectHistory: newProjectHistory,
					projectHistoryFuture: [],
					chordProgressionsByMeasure: newChordProgressionsByMeasure
				}, () => {
					this.nextprojectHistoryState = this.props.project.createCopy(true);
					if (this.state.isPlaying) { // For now, we will stop playback when any significant changes are made to the timeline
						MidiPlayer.stop();
					}

					// Beginner tour (in case the user deleted all of their timeline items)
					if (this.props.beginnerTourActive && !this.props.project.hasItems()) {
						this.props.jumpToTourStep(2);
					}
				});
			});
		} else {
			this.setState({
				...newState,
				projectHistory: newProjectHistory,
				projectHistoryFuture: []
			}, () => {
				this.nextprojectHistoryState = this.props.project.createCopy();
				if (this.state.isPlaying) { // For now, we will stop playback when any significant changes are made to the timeline
					MidiPlayer.stop();
				}
			});
		}
	}

	handleUndoButtonClick = () => {
		if (this.state.projectHistory.length <= 0) return;
		const newTimelinHistoryFuture = this.props.project.createCopy();
		this.props.updateProject(this.state.projectHistory[this.state.projectHistory.length - 1], () => {
			this.setState({
				projectHistory: this.state.projectHistory.slice(0, this.state.projectHistory.length - 1),
				projectHistoryFuture: [...this.state.projectHistoryFuture, newTimelinHistoryFuture]
			}, () => {
				this.nextprojectHistoryState = this.props.project.createCopy(true);
				if (this.state.isPlaying) { // For now, we will stop playback when any significant changes are made to the timeline
					MidiPlayer.stop();
				}
			});
		});
	}

	handleRedoButtonClick = () => {
		if (this.state.projectHistoryFuture.length <= 0) return;
		const newTimelinHistory = this.props.project.createCopy();
		this.props.updateProject(this.state.projectHistoryFuture[this.state.projectHistoryFuture.length - 1], () => {
			this.setState({
				projectHistory: [...this.state.projectHistory, newTimelinHistory],
				projectHistoryFuture: this.state.projectHistoryFuture.slice(0, this.state.projectHistoryFuture.length - 1)
			}, () => {
				this.nextprojectHistoryState = this.props.project.createCopy(true);
				if (this.state.isPlaying) { // For now, we will stop playback when any significant changes are made to the timeline
					MidiPlayer.stop();
				}
			});
		});
	}

	handleQuantizeToChord = async (measureNumber: number, chordProgression: BeatMappedChordProgression) => {
		const project = this.props.project.createCopy();
		const key = new Key(0); // C major/A minor by default (TODO: get key dynamically from project)
		project.quantizeMeasureToChordProgression(measureNumber, chordProgression, key);
		await this.setStateAndUpdateProjectHistory(this.state, project);
	}

	renderCursorMarker = () => {
		return (
			<Box
				className="cursor-marker-visual"
				ref={this.cursorMarkerRef}
				style={{
					height: this.state.timelineHeight - 1, // Subtract 1 to stop scroll from occuring
					pointerEvents: this.state.chopMode ? 'none' : 'auto',
				}}
				bg={"green"}
			>
				<div
					className="cursor-marker-handle"
					onMouseDown={this.handleMarkerMouseDown}
					onPointerDown={this.handleMarkerPointerDown}
				/>
			</Box>
		);
	};

	renderPlayheadMarker = () => {
		const { isPlaying } = this.state;
		return (
			<Box
				className="playhead-marker"
				ref={this.playheadMarkerRef}
				style={{ display: isPlaying ? 'block' : 'none' }}
				bg={"red.5"}
			/>
		);
	};

	render() {
		const {
			trackHeight,
			isPlaying,
			playingMeasureNumber,
			playbackLoading,
			chopMode,
			selectedItemIds,
			projectHistory,
			projectHistoryFuture,
			marqueeStartX,
			marqueeStartY,
			marqueeEndX,
			marqueeEndY,
			isMarqueeToggled,
			isMarqueeActive,
			notationView,
			cursorMarkerMeasure
		} = this.state;

		const { project, viewOnly, loadingInstrument } = this.props;

		const tempoQueryMeasure = isPlaying ? playingMeasureNumber : cursorMarkerMeasure;
		const currentTempo: Tempo = project.getLatestTempo(tempoQueryMeasure);

		const zoom = this.calculateCurrentZoom();
		const showBeatLines = zoom >= 2 && !notationView;
		return (
			<>
				<Box
					className="marquee-selection"
					style={{
						left: Math.min(marqueeStartX, marqueeEndX),
						top: Math.min(marqueeStartY, marqueeEndY),
						width: Math.abs(marqueeStartX - marqueeEndX),
						height: Math.abs(marqueeStartY - marqueeEndY),
						display: isMarqueeActive ? 'block' : 'none',
					}}
				/>
				<Box
					onContextMenu={this.handleContextMenu}
					className="beginner-tour-timeline-container"
					style={{
						height: "100%",
						width: "100%",
						display: "flex",
						flexDirection: "column",
						padding: this.props.deviceLayout.isTabletOrSmaller ? "0 1rem" : "0 2rem",
						overflow: "hidden",

						/* Stop text select when dragging */
						userSelect: "none",
						msUserSelect: "none",
						MozUserSelect: "none",
						WebkitUserSelect: "none",
						KhtmlUserSelect: "none"
					}}
					bg="white"
				>
					<Group className="top-controls" wrap="nowrap">
						<Group className="button-group" wrap="nowrap">
							{(!viewOnly) && (
								<>
									<Tooltip label={`Undo${!this.props.beginnerTourActive ? " (Ctrl + Z)" : ""}`} position="bottom" withArrow>
										<Button
											onClick={this.handleUndoButtonClick}
											ref={this.undoButtonRef}
											disabled={projectHistory.length === 0}
											size={this.props.deviceLayout.isPhoneOrSmaller ? "compact-sm" : "xs"}
										>
											<IconArrowBackUp size={20} />
										</Button>
									</Tooltip>

									<Tooltip label={`Redo${!this.props.beginnerTourActive ? " (Ctrl + Y)" : ""}`} position="bottom" withArrow>
										<Button
											onClick={this.handleRedoButtonClick}
											ref={this.redoButtonRef}
											disabled={projectHistoryFuture.length === 0}
											size={this.props.deviceLayout.isPhoneOrSmaller ? "compact-sm" : "xs"}
										>
											<IconArrowForwardUp size={20} />
										</Button>
									</Tooltip>
								</>
							)}
						</Group>
						{!this.props.deviceLayout.isPhoneOrSmaller && (
							<Box
								style={{ width: "40%" }}
							>
								<TimelineZoomBar
									zoom={zoom}
									onZoomChange={(zoom: number) => this.updateZoom(zoom)}
									zoomIntervals={Timeline.zoomIntervals}
								/>
							</Box>
						)}
						<Group className='button-group' wrap="nowrap">
							{(!viewOnly) && (
								<>
									<Tooltip label={`Selection Tool${!this.props.beginnerTourActive ? " (Shift)" : ""}`} position="bottom" withArrow>
										<Button
											onClick={this.handleMarqueeButtonClick}
											ref={this.marqueeButtonRef}
											variant={isMarqueeToggled ? "filled" : "outline"}
											size={this.props.deviceLayout.isPhoneOrSmaller ? "compact-sm" : "xs"}
										>
											<IconMarquee2 size={20} />
										</Button>
									</Tooltip>

									<Tooltip label={`Chop Tool${!this.props.beginnerTourActive ? " (Ctrl)" : ""}`} position="bottom" withArrow>
										<Button
											onClick={this.handleChopModeButtonClick}
											ref={this.chopModeButtonRef}
											variant={chopMode ? "filled" : "outline"}
											size={this.props.deviceLayout.isPhoneOrSmaller ? "compact-sm" : "xs"}
										>
											<IconScissors size={20} />
										</Button>
									</Tooltip>
								</>
							)}
						</Group>
					</Group>
					<div
						className="beginner-tour-timeline allow-vertical-scroll" // Beginner tour and allow vertical scroll
						data-timeline-dropzone="true"
						style={{
							display: "block",
							height: "100%",
							minHeight: "30vh",
							overflow: "scroll", /* Use auto for both scrollbars */
							position: "relative", /* Necessary for sticky positioning */
							borderRadius: "3px",
							outline: "2px solid #585858",
							cursor: isMarqueeToggled ? "crosshair" : chopMode ? "col-resize" : "auto",
							touchAction: "none"
						}}
						ref={this.timelineContainerRef}
						onMouseDown={this.handleMouseDown}
						onMouseMove={(e) => {
							this.handleMouseMove(e);
							this.handleMarkerMouseMove(e);
						}}
						onMouseUp={(e) => {
							this.handleMouseUp(e);
							this.handleMarkerMouseUp();
						}}
						onMouseLeave={this.handleMouseLeave}
						onPointerDown={this.handlePointerDownTimeline}
						onPointerMove={this.handlePointerMoveTimeline}
						onPointerUp={this.handlePointerUpTimeline}
						onPointerCancel={this.handlePointerCancelTimeline}
						onClick={this.handleClick}
						onScrollCapture={this.handleTimelineScrollCapture}
					>
						{project.tracks.length === 0 &&
							<Box
								className="empty-timeline-message"
								pos="absolute"
								inset={0}
								style={{
									pointerEvents: "none",          // no pointer events
									display: "flex",                // center content
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Text
									size={this.props.deviceLayout.isPhoneOrSmaller ? "sm" : "md"}
									maw="70%"
								>
									{this.props.deviceLayout.deviceLayout == DeviceLayout.PhoneLandscape ? (
										"Rotate your phone to access the library."
									) : (
										"Select a song from the library and drag in its parts to get started!"
									)}
								</Text>
							</Box>
						}
						{project.tracks.length > 0 &&
							<Box
								style={{
									position: "relative", /* Required for positioning the vertical lines */
									height: "100%",
									display: 'table'
								}}
							>
								<div
									className='marker-container'
									style={{
										left: Timeline.trackLabelWidth,
										width: `${Timeline.numberOfMeasures * this.state.measureWidth}px`,
									}}
								>
									{this.renderCursorMarker()}
									{this.renderPlayheadMarker()}
								</div>
								<Box>
									{/* Componentized markers and memoize them for faster renders */}
									<div
										className="timeline-measure-marker-container"
										onClick={this.handleMeasureMarkerClick}
									>
										<MeasureMarkers
											key={"measure-markers"}
											showBeatLines={showBeatLines}
											measureWidth={this.state.measureWidth}
											measureMarkerHeight={Timeline.measureMarkerHeight}
											trackLabelWidth={Timeline.trackLabelWidth}
											heightOfTracks={trackHeight * project.tracks.length}
											numberOfMeasures={Timeline.numberOfMeasures}
											isTabletOrSmaller={this.props.deviceLayout.isTabletOrSmaller}
											isPhoneOrSmaller={this.props.deviceLayout.isPhoneOrSmaller}
										/>
									</div>
									{project.tracks.length > 0 &&
										<div
											style={{ position: 'relative', height: '100%', width: `${Timeline.numberOfMeasures * this.state.measureWidth + Timeline.trackLabelWidth}px` }}
											onClick={this.handleClick}
										>
											<div
												className="track-label-top"
												style={{
													width: Timeline.trackLabelWidth,
													height: Timeline.measureMarkerHeight
												}}
											/>
											{project.tracks.map((track, i) => (
												<Track
													key={track.id}
													trackIndex={i}
													track={track}
													tighten={isPlaying || viewOnly}
													measureWidth={this.state.measureWidth}
													trackHeight={trackHeight}
													trackLabelWidth={Timeline.trackLabelWidth}
													addItemToSelectedItems={this.addItemToSelectedItems}
													removeItemFromSelectedItems={this.removeItemFromSelectedItems}
													clearSelectedItems={this.clearSelectedItems}
													startDraggingItems={this.startDraggingItems}
													startTrimmingItemsLeft={this.startTrimmingItemsLeft}
													startTrimmingItemsRight={this.startTrimmingItemsRight}
													selectedItems={selectedItemIds.filter((id) => track.items.flatMap((item) => item.id).includes(id))}
													chopMode={chopMode}
													chopItem={this.chopItem}
													notationView={notationView}
													draggingSections={this.props.draggingSections}
													isTutorialTrack={track.items.some(item => item.mostRecentInProject)}
													isPhoneOrSmaller={this.props.deviceLayout.isPhoneOrSmaller}
													isTabletOrSmaller={this.props.deviceLayout.isTabletOrSmaller}
												/>
											))}
											{/* Chord reductions if zoom level is greater than 3*/}
											{zoom >= 4 && (
												<ChordReductions
													runPitchQuantization={this.handleQuantizeToChord}
													onChordProgressionSelectionChange={(measure: number, chordProgression: BeatMappedChordProgression | undefined) => {
														const newChordProgressionsByMeasure = new Map(this.state.chordProgressionsByMeasure);
														const existingMapping = newChordProgressionsByMeasure.get(measure);
														if (existingMapping) {
															newChordProgressionsByMeasure.set(measure, {
																selection: chordProgression,
																options: existingMapping.options
															});
														} else {
															newChordProgressionsByMeasure.set(measure, {
																selection: chordProgression,
																options: chordProgression ? [chordProgression] : []
															});
														}
														this.setState({ chordProgressionsByMeasure: newChordProgressionsByMeasure });
													}}
													chordProgressionsByMeasure={this.state.chordProgressionsByMeasure}
													key={"chord-reduction-markers"}
													showBeatLines={showBeatLines}
													measureWidth={this.state.measureWidth}
													measureMarkerHeight={Timeline.measureMarkerHeight * 1.5}
													trackLabelWidth={Timeline.trackLabelWidth}
													heightOfTracks={trackHeight * project.tracks.length}
													numberOfMeasures={Timeline.numberOfMeasures}
													isTabletOrSmaller={this.props.deviceLayout.isTabletOrSmaller}
													isPhoneOrSmaller={this.props.deviceLayout.isPhoneOrSmaller}
												/>
											)}
										</div>
									}
								</Box>
							</Box>
						}
					</div>
					<Group className="bottom-controls" wrap="nowrap">
						<Group className="button-group" wrap="nowrap">
							{isPlaying ? (
								<Tooltip label="Stop (Spacebar)" position="top" withArrow>
									<Button
										onClick={this.handleStopButtonClick}
										ref={this.stopButtonRef}
										size={this.props.deviceLayout.isPhoneOrSmaller ? "compact-sm" : "xs"}
									>
										<IconPlayerStop size={20} />
									</Button>
								</Tooltip>
							) : (
								<Tooltip label={`Play${!this.props.beginnerTourActive ? " (Spacebar)" : ""}`} position="top" withArrow>
									<Button
										onClick={this.handlePlayButtonClick}
										ref={this.playButtonRef}
										disabled={this.getItems().length === 0 || playbackLoading || loadingInstrument}
										style={this.getItems().length > 0 ? { cursor: playbackLoading || loadingInstrument ? "progress" : "pointer" } : undefined}
										size={this.props.deviceLayout.isPhoneOrSmaller ? "compact-sm" : "xs"}
										className="beginner-tour-play-button" // Beginner tour
									>
										<IconPlayerPlay size={20} />
									</Button>
								</Tooltip>
							)}
							<Tooltip label="Rewind (R)" position="top" withArrow>
								<Button
									onClick={this.handleRewindButtonClick}
									ref={this.rewindButtonRef}
									size={this.props.deviceLayout.isPhoneOrSmaller ? "compact-sm" : "xs"}
								>
									<IconPlayerSkipBack size={20} />
								</Button>
							</Tooltip>
							{(!viewOnly) ? (
								<TempoControl
									initialTempo={currentTempo}
									onTempoChange={(tempo: Tempo) => this.handleTempoChangeCreation(0, tempo)}
									isPhoneOrSmaller={this.props.deviceLayout.isPhoneOrSmaller}
								/>
							) : (
								<Text>
									{currentTempo.getBpm()} BPM
								</Text>
							)}
						</Group>
						<Group className="button-group" wrap="nowrap">
							{(!viewOnly) && (
								<>
									<Tooltip label={`Delete Selection${!this.props.beginnerTourActive ? " (Backspace)" : ""}`} position="top" withArrow>
										<Button
											onClick={this.handleDeleteButtonClick}
											ref={this.deleteButtonRef}
											disabled={!(selectedItemIds?.length > 0)}
											size={this.props.deviceLayout.isPhoneOrSmaller ? "compact-sm" : "xs"}
										>
											<IconTrash size={20} />
										</Button>
									</Tooltip>

									<Tooltip label={`Duplicate Selection${!this.props.beginnerTourActive ? " (D)" : ""}`} position="top" withArrow>
										<Button
											onClick={this.handleDuplicateButtonClick}
											ref={this.duplicateButtonRef}
											disabled={!(selectedItemIds?.length > 0)}
											size={this.props.deviceLayout.isPhoneOrSmaller ? "compact-sm" : "xs"}
										>
											<IconCopy size={20} />
										</Button>
									</Tooltip>
								</>
							)}
						</Group>
					</Group>
				</Box>
			</>
		);
	}
}

export default Timeline;
